import {
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	IconButton,
	TableRow,
	Tooltip,
} from '@mui/material'
import React, { useState } from 'react'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import EditIcon from '@mui/icons-material/Edit'
import { TimeSheet } from '../../../../types/Reports/ShiftEnd/TimeSheet'
import { StyledTableCell } from '../../../core/StyledTableCell'
import AnimatedNumberField from '../../../core/AnimatedNumberField'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import tz from 'dayjs/plugin/timezone'
import ActivityRows from './ActivityRows'
import { useProjectFeaturesContext } from '../../../project/contexts/ProjectFeaturesContext'
import ConfirmDialog from '../../../core/ConfirmDialog'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import { usePermissionContext } from '../../../../security/PermissionContext'

dayjs.extend(utc)
dayjs.extend(tz)

type WorkerRowProps = {
	timesheet: TimeSheet
	openConfirmDialog: (timeSheetId: number) => void
	handleOverride: (timeSheetId: number) => Promise<void>
	handleRemoveTimesheet: (timesheetId: number) => Promise<void>
	handleAddTasks: (timeSheetId: number, timeRecordId?: number) => void
	tsSelectMode: string | null
	selectedWorkerIds: number[]
	setSelectedWorkerIds: (workerIds: number[]) => void
}

export function WorkerRow({
	timesheet,
	openConfirmDialog,
	handleOverride,
	handleRemoveTimesheet,
	handleAddTasks,
	tsSelectMode,
	selectedWorkerIds,
	setSelectedWorkerIds,
}: WorkerRowProps) {
	const [open, setOpen] = useState(false)
	const { projectTimeZone } = useProjectFeaturesContext()
	const { hasPermission } = usePermissionContext()
	const [isOverriding, setIsOverriding] = useState(false)
	const [showConfirmDialog, setShowConfirmDialog] = useState(false)

	const formatTime = (date: string | Date | undefined) => {
		if (!date) return 'n/a'
		return dayjs(date)
			.tz(projectTimeZone || 'UTC')
			.format('h:mm A')
	}

	const handleRevertClick = async () => {
		setIsOverriding(true)
		try {
			await handleOverride(timesheet.id)
		} finally {
			setIsOverriding(false)
			setShowConfirmDialog(false)
		}
	}

	const handleConfirmDialogOpen = () => {
		setShowConfirmDialog(true)
	}

	return (
		<>
			<TableRow key={timesheet.id}>
				<StyledTableCell width={10}>
					{tsSelectMode === 'select' && (
						<Checkbox
							checked={selectedWorkerIds.includes(timesheet.workerId!)}
							onChange={() => {
								if (selectedWorkerIds.includes(timesheet.workerId!)) {
									setSelectedWorkerIds(selectedWorkerIds.filter((id) => id !== timesheet.workerId!))
								} else {
									setSelectedWorkerIds([...selectedWorkerIds, timesheet.workerId!])
								}
							}}
							sx={{
								'& .MuiSvgIcon-root': {
									transition: 'transform 0.2s ease-in-out',
								},
								'&:hover .MuiSvgIcon-root': {
									transform: 'scale(1.1)',
								},
								p: 0,
								pr: 2,
								pl: 4,
							}}
						/>
					)}
					<IconButton aria-label='expand row' size='small' onClick={() => setOpen(!open)}>
						{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
					</IconButton>
				</StyledTableCell>
				<StyledTableCell align='center'>
					<Chip label={'Worker'} variant={'worker'} />
				</StyledTableCell>
				<StyledTableCell align='left'>{timesheet.title}</StyledTableCell>
				<StyledTableCell align='center'>
					{/* <Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'stretch',
							gap: 0.5,
						}}
					> */}
						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								gap: '2rem',
							}}
						>
							<span>Sign In: {formatTime(timesheet.signInAt)}</span>
							<span>Sign Out: {formatTime(timesheet.signOutAt)}</span>
						</div>
						{/* {timesheet.unpaidBreakHours != null && timesheet.unpaidBreakHours > 0 ? (
							<Box
								component='span'
								sx={{
									color: 'warning.dark',
									fontWeight: 600,
									fontStyle: 'italic',
									fontSize: '0.75rem',
									textAlign: 'left',
									lineHeight: 1.3,
									whiteSpace: 'normal',
									wordBreak: 'break-word',
									overflowWrap: 'anywhere',
								}}
							>
								Includes {timesheet.unpaidBreakHours}h unpaid break. <br />Work items are not progressed for
								break time.
							</Box>
						) : null}
					</Box> */}
				</StyledTableCell>
				<StyledTableCell align='right'>{formatTime(timesheet.startTime)}</StyledTableCell>
				<StyledTableCell align='right'>{formatTime(timesheet.endTime)}</StyledTableCell>
				<StyledTableCell align='right'>
					<AnimatedNumberField value={Number.isNaN(timesheet.hours) ? 0 : timesheet.hours} />
				</StyledTableCell>
				<StyledTableCell
					align='left'
					sx={{ whiteSpace: 'pre-line', wordWrap: 'break-word' }}
				></StyledTableCell>
				<StyledTableCell align='center' sx={{ width: 0 }}>
					{timesheet.confirmed ? (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
								justifyContent: 'center',
							}}
						>
							<Chip label={'Confirmed'} variant={'worker'} />
							{hasPermission(56) ? (
								<Tooltip title='Revert'>
									<Button
										variant='contained'
										color='info'
										size='small'
										onClick={handleConfirmDialogOpen}
										disabled={isOverriding}
										sx={{
											minWidth: 'auto',
											padding: '4px 8px',
										}}
									>
										{isOverriding ? (
											<CircularProgress size={20} color='inherit' sx={{ m: 0, p: 0 }} />
										) : (
											<EditIcon />
										)}
									</Button>
								</Tooltip>
							) : null}
						</Box>
					) : timesheet.activities.length === 0 ? (
						<IconButton color='error' onClick={() => handleRemoveTimesheet(timesheet.id)}>
							<RemoveCircleIcon />
						</IconButton>
					) : (
						<Button
							variant='contained'
							color='success'
							onClick={() => {
								openConfirmDialog(timesheet.id)
							}}
						>
							Confirm
						</Button>
					)}
				</StyledTableCell>
			</TableRow>
			{/* Displayed when down arrow icon is clicked. */}
			<ActivityRows open={open} timesheet={timesheet} handleAddTasks={handleAddTasks} />
			<ConfirmDialog
				title='Revert Confirmation'
				visible={showConfirmDialog}
				onConfirm={handleRevertClick}
				onCancel={() => setShowConfirmDialog(false)}
				confirmButtonText='Revert'
				cancelButtonText='Cancel'
				canConfirm={!isOverriding}
			>
				<div>
					<p>
						This action will affect the Actual Hours of Tasks, Work Items, and Foreman&apos;s PF.
					</p>
					<p>Are you sure you want to proceed?</p>
				</div>
			</ConfirmDialog>
		</>
	)
}
